import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/ui/Badge';
import { SectionCard } from '../../../components/ui/SectionCard';
import { Drawer, DrawerDetailGrid } from '../../../components/ui/Drawer';
import { STATUS_MAP, MISSING_FIELD_LABEL_KEYS, fmtDate, fmtAmount, isOverdue, convertToMAD } from '../utils';

export function InvoiceDrawer({ row, onClose }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  if (!row) return null;

  const cfg = STATUS_MAP[row.status] || { variant: 'info', labelKey: '' };
  const overdue = isOverdue(row);
  const totalInclVATMAD = row.currencyCode && row.currencyCode !== 'MAD'
    ? convertToMAD(row.totalInclVAT, row.currencyCode, row.exchangeRate)
    : null;
  const salesOrdersText = row.salesOrders?.map((o) => o.salesOrderNo).filter(Boolean).join(', ');
  const containersText = row.containers?.map((c) => c.containerNo).filter(Boolean).join(', ');

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      subtitle={t('invoices.drawer_subtitle')}
      title={row.invoiceNo}
      width="w-[520px]"
    >
      <SectionCard title={t('invoices.drawer_email_section')}>
        <DrawerDetailGrid
          fields={[
            { label: t('invoices.drawer_email_sender'),      value: row.emailSender },
            { label: t('invoices.drawer_email_received'),    value: fmtDate(row.receivedAt, locale) },
            { label: t('invoices.drawer_email_subject'),     value: row.emailSubject },
            { label: t('invoices.drawer_email_attachment'),  value: row.hasAttachment ? t('invoices.drawer_attachment_yes') : t('invoices.drawer_attachment_no') },
            { label: t('invoices.drawer_email_document'),    value: row.emailDetails?.documentName },
            { label: t('invoices.drawer_email_sent_at'),     value: fmtDate(row.emailDetails?.sentAt, locale) },
          ]}
        />
      </SectionCard>

      {row.isIncomplete && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          <p className="font-semibold">{t('invoices.incomplete_label')}</p>
          <p className="mt-0.5">
            {t('invoices.missing_fields_label')}{' '}
            {row.missingFields?.map((field) => t(MISSING_FIELD_LABEL_KEYS[field])).join(', ')}
          </p>
        </div>
      )}

      <SectionCard title={t('invoices.drawer_invoice_section')}>
        <DrawerDetailGrid
          fields={[
            { label: t('invoices.drawer_invoice_number'), value: row.invoiceNo, mono: true },
            { label: t('invoices.drawer_bl_number'),      value: row.billOfLadingNo || '—', mono: true },
            { label: t('invoices.drawer_supplier'),       value: row.shippingCompanyName },
            { label: t('invoices.drawer_invoice_date'),   value: fmtDate(row.postingDate, locale) },
            {
              label: t('invoices.drawer_due_date'),
              value: (
                <span className={overdue ? 'text-rose-600 font-semibold' : ''}>
                  {fmtDate(row.dueDate, locale)}
                  {overdue && <span className="ml-1 text-[11px]">({t('invoices.overdue_label')})</span>}
                </span>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard title={t('invoices.drawer_amounts_section')}>
        <div className="space-y-2 text-[13px]">
          <div className="flex justify-between text-slate-700">
            <span>{t('invoices.drawer_amount_ht')}</span>
            <span className="font-semibold tabular-nums">{fmtAmount(row.totalExclVAT, row.currencyCode)}</span>
          </div>
          <div className="flex justify-between text-slate-500 text-[12px]">
            <span>{t('invoices.drawer_vat')}</span>
            <span className="tabular-nums">{fmtAmount(row.totalVAT, row.currencyCode)}</span>
          </div>
          <div className="border-t border-slate-100 pt-2 flex justify-between font-bold text-slate-900">
            <span>{t('invoices.drawer_amount_ttc')}</span>
            <span className="tabular-nums">{fmtAmount(row.totalInclVAT, row.currencyCode)}</span>
          </div>
          {totalInclVATMAD != null && (
            <div className="flex justify-between text-slate-400 text-[12px]">
              <span>{t('invoices.drawer_amount_mad_equivalent')}</span>
              <span className="tabular-nums">{fmtAmount(totalInclVATMAD, 'MAD')}</span>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title={t('invoices.drawer_erp_section')}>
        <div className="space-y-2.5">
          <Badge variant={cfg.variant}>{t(cfg.labelKey)}</Badge>
          {row.erpSentAt ? (
            <p className="text-[12.5px] text-emerald-700 font-medium">
              {t('invoices.drawer_erp_sent_on')} {fmtDate(row.erpSentAt, locale)}
            </p>
          ) : (
            <p className="text-[12.5px] text-slate-400 italic">
              {t('invoices.drawer_erp_not_sent')}
            </p>
          )}
        </div>
      </SectionCard>

      <SectionCard title={t('invoices.drawer_shipping_section')}>
        <DrawerDetailGrid
          fields={[
            { label: t('invoices.drawer_sales_order'),     value: salesOrdersText || '—', mono: true },
            { label: t('invoices.drawer_container'),        value: containersText || '—', mono: true },
            { label: t('invoices.drawer_vessel'),           value: row.vesselName || '—' },
            { label: t('invoices.drawer_loading_port'),     value: row.loadingPort || '—' },
            { label: t('invoices.drawer_discharge_port'),   value: row.dischargePort || '—' },
            { label: t('invoices.drawer_departure_date'),   value: fmtDate(row.actualDepartureDate, locale) },
            { label: t('invoices.drawer_currency'),         value: row.currencyCode || '—' },
            { label: t('invoices.drawer_exchange_rate'),    value: row.exchangeRate != null ? row.exchangeRate.toFixed(6) : '—' },
          ]}
        />
        {row.physicalInvoiceLink && (
          <a
            href={row.physicalInvoiceLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-primary font-medium hover:underline"
          >
            {t('invoices.drawer_physical_invoice_link')}
          </a>
        )}
      </SectionCard>

      {row.containers?.length > 0 && (
        <SectionCard title={t('invoices.drawer_containers_section')}>
          <div className="flex flex-wrap gap-1.5">
            {row.containers.map((c, i) => (
              <span
                key={`${c.containerNo}-${i}`}
                className="inline-flex items-center gap-1 font-mono text-[11.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1"
              >
                {c.containerNo || '—'}
                {c.description && <span className="text-slate-400">· {c.description}</span>}
              </span>
            ))}
          </div>
        </SectionCard>
      )}

      {row.externalInvoiceLines?.length > 0 && (
        <SectionCard title={t('invoices.drawer_lines_section')}>
          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px] text-left">
              <thead>
                <tr className="border-b border-slate-100 text-slate-500">
                  <th className="pb-1.5 pr-2 font-medium">#</th>
                  <th className="pb-1.5 pr-2 font-medium">{t('invoices.drawer_line_description')}</th>
                  <th className="pb-1.5 pr-2 font-medium text-right">{t('invoices.drawer_line_qty')}</th>
                  <th className="pb-1.5 pr-2 font-medium">{t('invoices.drawer_line_unit')}</th>
                  <th className="pb-1.5 pr-2 font-medium text-right">{t('invoices.drawer_line_amount_fcy')}</th>
                  <th className="pb-1.5 font-medium text-right">{t('invoices.drawer_line_amount_lcy')}</th>
                </tr>
              </thead>
              <tbody>
                {row.externalInvoiceLines.map((line) => (
                  <tr key={line.lineNo} className="border-b border-slate-50 last:border-0">
                    <td className="py-1.5 pr-2 text-slate-400">{line.lineNo}</td>
                    <td className="py-1.5 pr-2 text-slate-700">{line.itemDescription}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{line.quantity}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{line.unitOfMeasure}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{line.amountExclVATFCY?.toLocaleString('fr-FR')}</td>
                    <td className="py-1.5 text-right tabular-nums font-medium text-slate-800">{line.amountExclVATLCY?.toLocaleString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </Drawer>
  );
}
